import { type FC, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { createBrowserRouter } from 'react-router-dom'
import { AppNavigationContext, useAppNavigation } from '#app/navigation/NavigationContext'
import { AppProvider } from '#app/routes/AppProvider'
import { FullscreenLoading } from '#components/FullScreenLoading'
import { PageRouteOutlet } from '#features/AppLayout/PageRouteOutlet'
import { DebugSettingsPopup } from '#features/DebugSettings/DebugSettingsPopup'
import { ErrorDisplayPage } from '#features/ErrorHandling/ErrorDisplay'
import { FullPageErrorBoundary } from '#features/ErrorHandling/FullPageErrorBoundary'
import { AppHeader, AppUserDropdown } from '#features/Header/AppHeader'
import { ManCodeOverlayProvider } from '#features/ManCode/ManCodeInformationOverlay'
import { AuthenticatedRoutes } from '#features/User/AuthenticatedRoutes'
import { buildAuthRouteFragments } from '#features/User/buildAuthRouteFragments'
import { ChangeBaseParametersOverlayProvider } from '#pages/Configuration/ChangeBaseParameters/ChangeBaseParametersContext'
import { configurationRoutes } from '#pages/Configuration/configurationRoutes'
import { EntryRouting } from './EntryRouting'

// biome-ignore lint/style/useComponentExportOnlyModules: does not need to be exported
const FrontendAppLayout: FC = () => {
  const { t } = useTranslation()
  const { navigate, routes } = useAppNavigation()
  const onClickHome = useCallback(() => {
    navigate(routes.index.make())
  }, [navigate, routes])

  return (
    <AppProvider>
      <ManCodeOverlayProvider>
        <ChangeBaseParametersOverlayProvider>
          <PageRouteOutlet appName={t('app.name', 'MAN Intelligent Configurator')} onLogoClick={onClickHome} />
          <DebugSettingsPopup />
        </ChangeBaseParametersOverlayProvider>
      </ManCodeOverlayProvider>
    </AppProvider>
  )
}

const AppErrorBoundary: FC = () => {
  return (
    <>
      <AppHeader />
      <ErrorDisplayPage />
    </>
  )
}

const AppErrorDisplay: FC = () => {
  const { t } = useTranslation()
  return (
    <AppProvider>
      <FullPageErrorBoundary appName={t('app.name', 'MAN Intelligent Configurator')} headerContent={<AppUserDropdown />} />
      <DebugSettingsPopup />
    </AppProvider>
  )
}

export const router = createBrowserRouter([
  {
    ErrorBoundary: AppErrorDisplay,
    element: (
      <AppNavigationContext>
        <FrontendAppLayout />
      </AppNavigationContext>
    ),
    hydrateFallbackElement: <FullscreenLoading />,
    children: [
      ...buildAuthRouteFragments(<AppHeader />),
      {
        element: <AuthenticatedRoutes pageHeader={<AppHeader />} />,
        children: [
          {
            index: true,
            element: <EntryRouting />,
            ErrorBoundary: AppErrorBoundary,
          },
          {
            path: '/setup',
            lazy: async () => ({ Component: (await import('../../pages/TechnicalSetup')).TechnicalSetup }),
          },
          {
            path: '/landing',
            lazy: async () => ({ Component: (await import('../../pages/Landing')).Landing }),
            ErrorBoundary: AppErrorBoundary,
          },
          configurationRoutes,
        ],
      },
      {
        path: '/bridge',
        lazy: async () => ({ Component: (await import('../../pages/Bridge')).Bridge }),
        ErrorBoundary: AppErrorBoundary,
      },
      {
        path: '/cache-warming',
        lazy: async () => ({ Component: (await import('../../pages/CacheWarming')).CacheWarming }),
        ErrorBoundary: AppErrorBoundary,
      },
    ],
  },
])
